// Main-thread wrapper around `src/sim/replayWorker.ts`, in the shape of `src/sim/client.ts`'s
// `SimClient`: the landing card never talks to the worker directly, just awaits one promise.

import type { AoMeta, Dials, Scenario } from './types';
import type { OrderLogEntry } from './sim';
import type { ReplayDone, ReplayRequest } from './replayWorker';

export type ReplayResult = Omit<ReplayDone, 'type'>;

/** Runs one order log to `untilTick` off the main thread and resolves with the reconstructed payload
 * and its hash. The worker terminates itself once it has answered - a shared fight is opened once, not
 * kept around for more runs. */
export function runReplay(args: {
  meta: AoMeta;
  buffers: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer };
  scenario: Scenario;
  dials: Dials;
  seed: number;
  orderLog: OrderLogEntry[];
  untilTick: number;
}): Promise<ReplayResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./replayWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<ReplayDone>) => {
      worker.terminate();
      resolve(e.data);
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(e.error instanceof Error ? e.error : new Error(e.message || 'replay worker failed'));
    };
    const req: ReplayRequest = {
      type: 'run',
      meta: args.meta, buffers: args.buffers, scenario: args.scenario, dials: args.dials,
      seed: args.seed, orderLog: args.orderLog, untilTick: args.untilTick,
    };
    worker.postMessage(req);
  });
}
